/**
 * POS smart-input parser.
 *
 * Power-user typing in the search box:
 *   5*coke   → qtyAdd  (add Coke ×5)
 *   #1001    → codeAdd (exact item code lookup)
 *   +50      → discAmt (set selected line discount = 50)
 *   -10%     → discPct (set selected line discount = 10%)
 *   =200     → price   (set selected line unit price = 200)
 *   anything else → search (fuzzy match the product grid)
 */
export const parsePOSInput = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return { type: 'empty' };

  // =200 → set price
  if (s.startsWith('=')) {
    const v = Number(s.slice(1));
    if (Number.isFinite(v) && v >= 0) return { type: 'price', value: v };
  }

  // +50 → discount amount
  if (s.startsWith('+')) {
    const v = Number(s.slice(1));
    if (Number.isFinite(v) && v >= 0) return { type: 'discAmt', value: v };
  }

  // -10% → discount percent  (also -50 → discount amount, kept for ergonomics)
  if (s.startsWith('-')) {
    if (s.endsWith('%')) {
      const v = Number(s.slice(1, -1));
      if (Number.isFinite(v) && v >= 0 && v <= 100) return { type: 'discPct', value: v };
    } else {
      const v = Number(s.slice(1));
      if (Number.isFinite(v) && v >= 0) return { type: 'discAmt', value: v };
    }
  }

  // #1001 → item code add
  if (s.startsWith('#')) {
    const code = s.slice(1).trim();
    if (code) return { type: 'codeAdd', code };
  }

  // N*term  or  N x term  → qty-add
  const m = s.match(/^(\d+(?:\.\d+)?)\s*[\*x]\s*(.+)$/i);
  if (m) {
    const qty = Number(m[1]);
    const term = m[2].trim();
    if (qty > 0 && term) return { type: 'qtyAdd', qty, term };
  }

  return { type: 'search', term: s };
};

/**
 * Best-effort fuzzy item match.
 * Strategy:
 *   1. exact barcode match
 *   2. exact itemCode match
 *   3. case-insensitive substring on itemName / itemCode / description
 *   4. first-letter acronym match on itemName (e.g. "CC" → "Coca Cola")
 * Returns the matching item or null.
 */
export const findItemByTerm = (items, term) => {
  if (!items || !term) return null;
  const q = String(term).trim();
  if (!q) return null;

  const byBarcode = items.find(i => i.barcode && i.barcode === q);
  if (byBarcode) return byBarcode;

  const byCode = items.find(i => (i.itemCode || '').toLowerCase() === q.toLowerCase());
  if (byCode) return byCode;

  const lc = q.toLowerCase();
  const byName = items.find(i =>
    (i.itemName || '').toLowerCase().includes(lc) ||
    (i.itemCode || '').toLowerCase().includes(lc) ||
    (i.description || '').toLowerCase().includes(lc)
  );
  if (byName) return byName;

  // Acronym: "CC" → "Coca Cola", "PB" → "Pepsi Bottle"
  if (q.length >= 2 && /^[A-Za-z]+$/.test(q)) {
    const upper = q.toUpperCase();
    const byAcronym = items.find(i => {
      const initials = String(i.itemName || '').split(/\s+/).map(w => w[0] || '').join('').toUpperCase();
      return initials.startsWith(upper);
    });
    if (byAcronym) return byAcronym;
  }

  return null;
};

export default { parsePOSInput, findItemByTerm };
